import { FC, useEffect } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import Alert from '@/storybook/components/Alert/Alert';
import Button from '@/storybook/components/Button/Button';
import Dialog from '@/storybook/components/Dialog/Dialog';
import Input from '@/storybook/components/FormCollection/Input/Input';
import Text from '@/storybook/components/Text/Text';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './RenameConversationDialog.module.css';

const TITLE_MAX_LENGTH = 100;

interface RenameFormValues {
  title: string;
}

interface RenameConversationDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  value: string;
  errorMessage?: string | null;
  onSave: (title: string) => void;
  onCancel: () => void;
  onClearError?: () => void;
}

const RenameConversationDialog: FC<RenameConversationDialogProps> = ({
  isOpen,
  isLoading,
  value,
  errorMessage,
  onSave,
  onCancel,
  onClearError,
}) => {
  const { t } = useTranslation();

  const formMethods = useForm<RenameFormValues>({ defaultValues: { title: value.slice(0, TITLE_MAX_LENGTH) } });
  const { handleSubmit, reset, watch } = formMethods;

  useEffect(() => {
    if (isOpen) {
      reset({ title: value.slice(0, TITLE_MAX_LENGTH) });
    }
  }, [isOpen, value, reset]);

  const titleValue = watch('title') ?? '';
  const titleLength = titleValue.length;
  const isAtMaxLength = titleLength >= TITLE_MAX_LENGTH;
  const isRenameDisabled = titleValue.trim().length === 0 || isLoading;

  const submit = handleSubmit(({ title }) => {
    const trimmed = title.trim();

    if (trimmed && !isLoading) {
      onSave(trimmed);
    }
  });

  const footer = (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={isLoading}
        data-testid={CHAT_HISTORY_TEST_IDS.renameDialogCancel}
      >
        {t('components.aiChatDrawer.history.renameDialog.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={submit}
        disabled={isRenameDisabled}
        loading={isLoading}
        data-testid={CHAT_HISTORY_TEST_IDS.renameDialogSave}
      >
        {t('components.aiChatDrawer.history.renameDialog.rename')}
      </Button>
    </>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={t('components.aiChatDrawer.history.renameDialog.title')}
      size="L"
      footer={footer}
      data-testid={CHAT_HISTORY_TEST_IDS.renameDialog}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={submit}>
          <Input
            name="title"
            className={styles.renameInput}
            registerOptions={{
              maxLength: TITLE_MAX_LENGTH,
              onChange: () => onClearError?.(),
            }}
            maxLength={TITLE_MAX_LENGTH}
            aria-label={t('components.aiChatDrawer.history.renameDialog.inputLabel')}
            autoFocus
            data-testid={CHAT_HISTORY_TEST_IDS.renameDialogInput}
          />
          <Text
            as="p"
            aria-live="polite"
            className={cx(styles.counter, { [styles.counterMax]: isAtMaxLength })}
            data-testid={CHAT_HISTORY_TEST_IDS.renameDialogCounter}
          >
            {isAtMaxLength
              ? t('components.aiChatDrawer.history.renameDialog.counterMax', { max: TITLE_MAX_LENGTH })
              : t('components.aiChatDrawer.history.renameDialog.counter', {
                  current: titleLength,
                  max: TITLE_MAX_LENGTH,
                })}
          </Text>
          {errorMessage ? (
            <Alert
              variant="error"
              className={styles.errorAlert}
              message={errorMessage}
              data-testid={CHAT_HISTORY_TEST_IDS.renameDialogError}
            />
          ) : null}
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default RenameConversationDialog;
